import { getRangeFromString, getEstimateReportData } from "../workbook";
import { fetchEstimateExport } from "../../api";
import { transformUseGroups } from "../datasources/useGroups";
import {
  CHART_PREFIX,
  colorizeChart,
  getChartAndTable,
  getChartDimensions,
  getNextChartIndex,
  setChartTitleAndSubtitle,
  upsertChartTable,
} from "./helpers/common";
import { ChartIcon, ChartIconGetter, EdiphiChartType } from "./api";
import { ChartOption, ChartShape, ChartSize } from "./api";
import { NotFoundError } from "../../errors";

const USE_GROUP_DESERTS_CHART_TYPE = "useGroupDeserts";
const MY_TITLE = "Use Group Distribution";

const useGroupDesertsOptions: Record<string, ChartOption> = {
  chartType: {
    key: "chartType",
    label: "Chart Type",
    type: "toggle",
    selectedOptionItem: {
      key: "donut",
      label: "",
      icon: { src: "assets/icons/FcDonutChart.svg", alt: "Donut Chart" },
    },
    optionItems: {
      donut: {
        key: "donut",
        label: "",
        icon: { src: "assets/icons/FcDonutChart.svg", alt: "Donut Chart" },
      },
      pie: {
        key: "pie",
        label: "",
        icon: { src: "assets/icons/FcPieChart.svg", alt: "Pie Chart" },
      },
    },
    getSelectedOptionItem: async function (worksheetName, chartName) {
      return Excel.run(async (context) => {
        const [chart] = await getChartAndTable(context, worksheetName, chartName);
        chart.load("chartType");
        await context.sync();

        if (chart.chartType === Excel.ChartType.doughnut) {
          return this.optionItems["donut"];
        } else if (chart.chartType === Excel.ChartType.pie) {
          return this.optionItems["pie"];
        }

        return this.optionItems["donut"];
      });
    },
    onChange: async (worksheetName, chartName, option) => {
      return Excel.run(async (context) => {
        const [chart] = await getChartAndTable(context, worksheetName, chartName);
        chart.chartType = option.key === "donut" ? Excel.ChartType.doughnut : Excel.ChartType.pie;
        await context.sync();
      });
    },
  },
  breakdown: {
    key: "breakdown",
    label: "Breakdown",
    type: "toggle",
    selectedOptionItem: { key: "total", label: "$" },
    optionItems: {
      total: { key: "total", label: "$", colIndex: 1 },
      gsf: { key: "gsf", label: "GSF", colIndex: 2 },
      total_per_gsf: { key: "total_per_gsf", label: "$/GSF", colIndex: 3 },
    },
    getSelectedOptionItem: async function (worksheetName, chartName) {
      return Excel.run(async (context) => {
        // 1) fetch the chart & table handles
        const [chart, _] = await getChartAndTable(context, worksheetName, chartName);

        const dimStringResult = chart.series.getItemAt(0).getDimensionDataSourceString("Values");
        await context.sync();
        const address = dimStringResult.value;
        const range = await getRangeFromString(context, address);
        if (!range) {
          return this.optionItems["total"];
        }
        const headerCell = range.getCell(-1, 0);
        headerCell.load("values");
        await context.sync();
        const headerValue = headerCell.values[0][0];
        const matchingOption = Object.values(this.optionItems).find((opt) => opt.key === headerValue);
        if (!matchingOption) {
          return this.optionItems["total"];
        }
        return matchingOption;
      });
    },
    onChange: async (worksheetName, chartName, option) => {
      return Excel.run(async (context) => {
        const [chart, table] = await getChartAndTable(context, worksheetName, chartName);

        const series = chart.series.getItemAt(0);
        const dataColumn = table.columns.getItem(option.key);
        const dataRange = dataColumn.getDataBodyRange();
        await context.sync();

        series.setValues(dataRange);
        series.name = option.label;
        setChartTitleAndSubtitle(chart, MY_TITLE, option.label.replace("$", "Cost"));
        await colorizeChart(chart, context);
        await context.sync();
      });
    },
  },
};

const chartBuilder = async (options: Record<string, ChartOption>) => {
  const chartIndex = await getNextChartIndex();
  const chartName = `${CHART_PREFIX}${USE_GROUP_DESERTS_CHART_TYPE}_${chartIndex}`;
  await upsertChartData(chartName);
  const chartTypeKey = options["chartType"]?.selectedOptionItem.key || "donut";

  const chart = await addChart({
    chartType: chartTypeKey === "donut" ? Excel.ChartType.doughnut : Excel.ChartType.pie,
    chartTitle: MY_TITLE,
    chartSubTitle: "Totals",
    name: chartName,
    shape: "square",
    size: "medium",
  });

  return chart;
};

const chartRefresher = async (worksheetName: string, chartName: string, options?: Record<string, ChartOption>) => {
  await upsertChartData(chartName);
  await Excel.run(async (context) => {
    const [chart, table] = await getChartAndTable(context, worksheetName, chartName);
    const series = chart.series.getItemAt(0);
    const breakdownOption = options?.breakdown;
    const optionItem = breakdownOption?.optionItems[breakdownOption.selectedOptionItem.key];
    const dataColumn = table.columns.getItem(optionItem?.key || "total");
    const dataRange = dataColumn.getDataBodyRange();
    const categoryRange = table.columns.getItem("Use Group").getDataBodyRange();
    series.setValues(dataRange);
    series.setXAxisValues(categoryRange);
    await colorizeChart(chart, context);
    await context.sync();
  });
};

const upsertChartData = async (chartName: string) => {
  const createDataRows = async () => {
    const reportData = await getEstimateReportData();
    const estimateId = reportData[0]?.estimate_id;
    if (!estimateId) return [];
    const result = await fetchEstimateExport(estimateId);
    if (!result.success || !result.data) {
      throw new NotFoundError("Estimate data not found for chart", { estimateId });
    }
    const useGroups = transformUseGroups(
      result.data?.estimate?.stats?.useGroups ?? [],
      result.data?.floor_groups ?? []
    );
    const dataRows: Record<string, string | number | null>[] = [];

    const sortedUseGroups = [...useGroups].sort((a, b) => a.order - b.order);

    for (const useGroup of sortedUseGroups) {
      dataRows.push({
        "Use Group": useGroup.name,
        total: useGroup.costOfTradesSubtotal,
        gsf: useGroup.gsf,
        total_per_gsf: useGroup.gsf ? useGroup.costOfTradesSubtotal / useGroup.gsf : 0,
      });
    }

    return dataRows;
  };

  const dataRows = await createDataRows();

  await upsertChartTable(dataRows, chartName);
};

const addChart = async (params: {
  chartType: Excel.ChartType;
  chartTitle: string;
  chartSubTitle: string;
  name: string;
  shape: ChartShape;
  size: ChartSize;
}) => {
  return Excel.run(async (context) => {
    const workbook = context.workbook;
    const activeSheet = workbook.worksheets.getActiveWorksheet();
    const table = workbook.tables.getItemOrNullObject(params.name);
    await context.sync();
    if (table.isNullObject) {
      throw new NotFoundError("Table not found", { tableName: params.name });
    }

    const range = table.getRange().getColumn(0).getResizedRange(0, 1);
    await context.sync();
    const chart = activeSheet.charts.add(params.chartType, range, Excel.ChartSeriesBy.columns);
    chart.name = params.name;
    setChartTitleAndSubtitle(chart, params.chartTitle, params.chartSubTitle);

    const { width, height } = getChartDimensions(params.shape, params.size);
    chart.height = height;
    chart.width = width;
    if (params.chartType === Excel.ChartType.doughnut) {
      const series = chart.series.getItemAt(0);
      //series.load("doughnutHoleSize");
      //await context.sync();
      series.doughnutHoleSize = 40;
    }
    await colorizeChart(chart, context);
    styleChart(chart);
    await context.sync();
    return chart;
  });
};

const styleChart = (chart: Excel.Chart) => {
  chart.legend.format.font.size = 12;
  chart.legend.format.font.bold = true;
};

const getOptions = async (worksheetName: string, chartName: string): Promise<Record<string, ChartOption>> => {
  const options = { ...useGroupDesertsOptions };

  for (const option of Object.values(options)) {
    if (option.getSelectedOptionItem) {
      try {
        const currentValue = await option.getSelectedOptionItem(worksheetName, chartName);
        // Guarded because these option objects are shared: the spread above is shallow, so writing an
        // unresolved value here would replace the declared default until the module is re-imported.
        if (currentValue) option.selectedOptionItem = currentValue;
      } catch (error) {
        // ???: Change to logger.warn?
        console.warn(`Failed to get current value for option ${option.key}:`, error);
      }
    }
  }

  return options;
};

const chartIconGetter: ChartIconGetter = (chartType?: Excel.ChartType): ChartIcon => {
  const chartTypeLower = chartType?.toString().toLowerCase() || "doughnut";
  if (chartTypeLower.includes("doughnut")) {
    return { src: "assets/icons/FcDonutChart.svg", alt: "Donut Chart" };
  } else {
    return { src: "assets/icons/FcPieChart.svg", alt: "Pie Chart" };
  }
};

export const useGroupDesertsChartType: EdiphiChartType = {
  key: USE_GROUP_DESERTS_CHART_TYPE,
  title: "Use Group Distribution",
  chartBuilder,
  chartRefresher,
  chartIconGetter,
  getOptions,
};
